import { logActivity } from '../utils/activityLog';
import React, { useState, useEffect, useRef } from 'react';
import { Filter, MoreVertical, Download, Upload, Plus, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileSpreadsheet, ChevronDown, UserPlus, Lock, Unlock, RefreshCw, Building, Globe, X, Search, Trash2, Copy, Mail, Phone, User, GitMerge } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { clientsService } from '../services/clients';
import { exportService } from '../services/export';
import api from '../services/api';
import { toast } from 'sonner';
import { STATUS_PALETTE } from './Settings';
import { getCurrentUser, getHeadRoles, hasPermission } from '../utils/roles';
import { userSettingsService } from '../services/userSettings';
import MultiSelectDropdown from '../components/MultiSelectDropdown';

// agent / desk / team are multi-select (arrays); the rest are single-value strings.
const MULTI_FILTER_KEYS = ['agent', 'desk', 'team'];
const EMPTY_FILTERS = { search: '', status: '', country: '', office: '', agent: [], language: '', department: '', desk: [], team: [], ftd: '', affiliate: '', salesStatus: '', retStatus: '', refSearch: '', balanceMin: '', balanceMax: '', regFrom: '', regTo: '', modFrom: '', modTo: '', active: '', needsUpdate: '' };

// All available columns definition
const ALL_COLUMNS = [
  { key: 'id',           label: 'ID' },
  { key: 'active',       label: 'Active' },
  { key: 'office',       label: 'Office' },
  { key: 'client',       label: 'Client' },
  { key: 'email',        label: 'Email' },
  { key: 'phone',        label: 'Phone' },
  { key: 'salesStatus',  label: 'Sales Status' },
  { key: 'retStatus',    label: 'Retention Status' },
  { key: 'language',     label: 'Language' },
  { key: 'country',      label: 'Country' },
  { key: 'department',   label: 'Department' },
  { key: 'agent',        label: 'Agent' },
  { key: 'balance',      label: 'Balance' },
  { key: 'affiliate',    label: 'Affiliate' },
  { key: 'lastComment',  label: 'Last Comment' },
  { key: 'lastCommentDate', label: 'Last Comment Date' },
  { key: 'withdrawals',  label: 'Withdrawals' },
  { key: 'currency',     label: 'Currency' },
  { key: 'totalDeposits', label: 'Total Deposits' },
  { key: 'lastLogin',    label: 'Last login client' },
  { key: 'registeredAt', label: 'Registered At' },
  { key: 'registrationIp', label: 'Registration IP' },
  { key: 'funnel',       label: 'Funnel' },
  { key: 'additionalInfo', label: 'Additional Info' },
];

const DEFAULT_VISIBLE = new Set([
  'active','office','client','email','phone',
  'salesStatus','retStatus','language','country','department','agent',
]);

const CLIENT_COLUMNS_SETTING_KEY = 'clients.visibleColumns';
const CLIENT_COLUMN_WIDTHS_SETTING_KEY = 'clients.columnWidths';
const CLIENT_LIST_STATE_SETTING_KEY = 'clients.listState';
const ALL_COLUMN_KEYS = new Set(ALL_COLUMNS.map(col => col.key));
const SORTABLE_COLUMN_KEYS = new Set(ALL_COLUMNS.map(col => col.key));
const PAGE_SIZE_OPTIONS = new Set([0, 10, 25, 50, 100, 1000]);
const MIN_COLUMN_WIDTH = 72;
const MAX_COLUMN_WIDTH = 600;
const SELECTION_COLUMN_WIDTH = 52;
const DEFAULT_COLUMN_WIDTHS = {
  id: 90,
  active: 90,
  office: 150,
  client: 190,
  email: 230,
  phone: 150,
  salesStatus: 150,
  retStatus: 170,
  language: 120,
  country: 140,
  department: 150,
  agent: 170,
  balance: 130,
  affiliate: 150,
  lastComment: 260,
  lastCommentDate: 180,
  withdrawals: 140,
  currency: 110,
  totalDeposits: 150,
  lastLogin: 180,
  registeredAt: 180,
  registrationIp: 160,
  funnel: 150,
  additionalInfo: 260,
};

const toVisibleColumnSet = (keys) => {
  const safeKeys = Array.isArray(keys) ? keys.filter(key => ALL_COLUMN_KEYS.has(key)) : [];
  return new Set(safeKeys.length ? safeKeys : DEFAULT_VISIBLE);
};

const sanitizeColumnWidths = (value) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return Object.fromEntries(ALL_COLUMNS.map(({ key }) => {
    const width = Number(source[key]);
    const safeWidth = Number.isFinite(width)
      ? Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width)))
      : DEFAULT_COLUMN_WIDTHS[key];
    return [key, safeWidth];
  }));
};

const sanitizeFilters = (value) => {
  const merged = { ...EMPTY_FILTERS, ...(value || {}) };
  // Coerce multi-select keys to arrays (older persisted state stored strings).
  for (const key of MULTI_FILTER_KEYS) {
    merged[key] = Array.isArray(merged[key]) ? merged[key] : (merged[key] ? [merged[key]] : []);
  }
  return merged;
};

const sanitizeSortConfig = (value) => {
  const key = SORTABLE_COLUMN_KEYS.has(value?.key) ? value.key : 'id';
  const direction = value?.direction === 'asc' ? 'asc' : 'desc';
  return { key, direction };
};

const sanitizePage = (value) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const sanitizePageSize = (value) => {
  const size = Number(value);
  return PAGE_SIZE_OPTIONS.has(size) ? size : 25;
};

const formatClientTableDate = (value) => {
  if (!value) return '-';
  const dateValue = typeof value === 'string' && !value.endsWith('Z') && !/[+\-]\d{2}:\d{2}$/.test(value)
    ? new Date(`${value}Z`)
    : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return '-';

  const pad = (part) => String(part).padStart(2, '0');
  return `${pad(dateValue.getDate())}/${pad(dateValue.getMonth() + 1)}/${dateValue.getFullYear()}, ${pad(dateValue.getHours())}:${pad(dateValue.getMinutes())}`;
};

const normalizeDepartment = (value = '') => {
  const normalized = String(value || '').toLowerCase().trim();
  if (normalized.startsWith('sales')) return 'sales';
  if (normalized.startsWith('ret') || normalized.includes('retention')) return 'retention';
  return normalized;
};

const needsClientUpdate = (client) => {
  if (typeof client.needsUpdate === 'boolean') return client.needsUpdate;
  return Number.isFinite(client.lastInfoDays) && client.lastInfoDays >= 3;
};

const clientUpdateTitle = (client) => {
  if (!needsClientUpdate(client)) return undefined;
  const days = Number.isFinite(client.lastInfoDays) ? client.lastInfoDays : 3;
  return `No updated information for ${days}+ days`;
};

const Clients = () => {
  const initialListState = useRef({});
  const [currentPage, setCurrentPage] = useState(() => sanitizePage(initialListState.current.currentPage));
  const [itemsPerPage, setItemsPerPage] = useState(() => sanitizePageSize(initialListState.current.itemsPerPage));
  const [selectedClients, setSelectedClients] = useState([]);
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [clientPrefsLoaded, setClientPrefsLoaded] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // bulk action modal
  const [modalValue, setModalValue] = useState('');
  const [options, setOptions] = useState({ agents: [], offices: [], countries: [], statuses: [], languages: [], desks: [], teams: [], affiliates: [] });
  const [statusColorMap, setStatusColorMap] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState(() => sanitizeSortConfig(initialListState.current.sortConfig));

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(() => sanitizeFilters(initialListState.current.filters || initialListState.current.appliedFilters));
  const [appliedFilters, setAppliedFilters] = useState(() => sanitizeFilters(initialListState.current.appliedFilters));

  // Column visibility (Metrics dropdown)
  const [showMetrics, setShowMetrics] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => new Set(DEFAULT_VISIBLE));
  const metricsRef = useRef(null);

  // Per-column widths (drag-resizable, persisted per user)
  const [columnWidths, setColumnWidths] = useState(() => ({ ...DEFAULT_COLUMN_WIDTHS }));
  const columnWidthsRef = useRef({ ...DEFAULT_COLUMN_WIDTHS });
  const resizeCleanupRef = useRef(null);

  // Top-bar search (debounced, applied live — independent of the filter panel's Apply)
  const searchDebounceRef = useRef(null);

  // Duplicates
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicateCriterion, setDuplicateCriterion] = useState('name');
  const [duplicatesSearched, setDuplicatesSearched] = useState(false);
  const [mergeReview, setMergeReview] = useState(null);        // { groupKey, clients, primaryId }
  const [mergeFieldSources, setMergeFieldSources] = useState({});
  const [mergeReviewLoading, setMergeReviewLoading] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);

  // Import
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importDragging, setImportDragging] = useState(false);
  const [importStep, setImportStep] = useState('idle'); // 'idle' | 'mapping'
  const [importColumns, setImportColumns] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [importPreset, setImportPreset] = useState({ agent_id: '', department: '', desk: '', team: '' });
  const importFileRef = useRef(null);

  // Fields that come from the imported file columns
  const IMPORT_FIELDS = [
    { key: 'email',      label: 'Email',      required: true  },
    { key: 'first_name', label: 'First Name', required: true  },
    { key: 'last_name',  label: 'Last Name',  required: false },
    { key: 'phone',      label: 'Phone',      required: false },
    { key: 'mobile',     label: 'Mobile',     required: false },
    { key: 'status',     label: 'Status',     required: false },
    { key: 'office',     label: 'Office',     required: false },
    { key: 'country',    label: 'Country',    required: false },
    { key: 'language',   label: 'Language',   required: false },
    { key: 'credits',    label: 'Credits',    required: false },
    { key: 'source',     label: 'Source',     required: false },
    { key: 'referral',   label: 'Referral',   required: false },
    { key: 'funnel',     label: 'Funnel',     required: false },
    { key: 'registration_ip', label: 'Registration IP', required: false },
    { key: 'affiliate',  label: 'Affiliate',  required: false },
    { key: 'ext_id',     label: 'Ext. ID',    required: false },
  ];

  const FIELD_ALIASES = {
    email:      ['email', 'e-mail', 'mail'],
    first_name: ['first_name', 'firstname', 'first name', 'name'],
    last_name:  ['last_name', 'lastname', 'last name', 'surname'],
    phone:      ['phone', 'phone number', 'tel'],
    mobile:     ['mobile', 'mobile number', 'cell'],
    status:     ['status'],
    office:     ['office'],
    country:    ['country'],
    language:   ['language'],
    credits:    ['credits'],
    source:     ['source'],
    referral:   ['referral'],
    funnel:     ['funnel'],
    registration_ip: ['registration_ip', 'registration ip', 'reg ip', 'reg. ip', 'ip'],
    affiliate:  ['affiliate'],
    ext_id:     ['ext_id', 'external id', 'affiliate id', 'ext id'],
  };

  const autoMatchColumns = (cols) => {
    const m = {};
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
      const match = cols.find(c => aliases.includes((c || '').toLowerCase().trim()));
      m[field] = match || '';
    }
    return m;
  };

  const closeImportModal = () => {
    setShowImport(false); setImportFile(null);
    setImportStep('idle'); setImportColumns([]); setImportMapping({});
    setImportPreset({ agent_id: '', department: '', desk: '', team: '' });
  };

  // New Client
  const [showNewClient, setShowNewClient] = useState(false);
  const [newClientData, setNewClientData] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    status: 'New', office: '', country: '', language: '',
    department: '', agent: '', desk: '', team: '', credits: '',
    affiliate: '', ext_id: '', source: '', referral: '', funnel: '', registrationIp: '',
  });
  const [newClientLoading, setNewClientLoading] = useState(false);
  const [allAgentOpts, setAllAgentOpts] = useState([]);  // for new-client modal
  const [allUsers, setAllUsers] = useState([]);           // for bulk-assign dropdown

  // Assign modal cascade filters
  const [assignDeskOpts, setAssignDeskOpts] = useState([]);   // [{id, name}]
  const [assignTeamOpts, setAssignTeamOpts] = useState([]);   // [{id, name}]
  const [assignDesk,     setAssignDesk]     = useState('');   // selected desk name
  const [assignTeam,     setAssignTeam]     = useState('');   // selected team name
  const [assignStatus,   setAssignStatus]   = useState('');   // optional status after assign

  const actionsRef = useRef(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const currentUser = getCurrentUser();
  const canDelete = hasPermission(currentUser, 'Delete Client');
  const canMerge = ['owner', 'admin', 'crm_manager'].includes(currentUser?.role);
  const canCreateClient = hasPermission(currentUser, 'Create Client');
  const canImportClients = hasPermission(currentUser, 'Import Clients');
  const canExportClients = hasPermission(currentUser, 'Export Clients');
  const columnLabel = (key, fallback) => {
    const translated = t(`clients.columns.${key}`);
    return translated === `clients.columns.${key}` ? fallback : translated;
  };
  const departmentLabel = (value) => {
    const normalized = normalizeDepartment(value);
    if (normalized === 'sales') return t('clients.departments.sales');
    if (normalized === 'retention') return t('clients.departments.retention');
    return value || '-';
  };

  useEffect(() => { logActivity('view', 'clients', null, 'Viewed Clients list'); }, []);

  useEffect(() => {
    if (!clientPrefsLoaded) return;
    loadClients();
  }, [clientPrefsLoaded, currentPage, itemsPerPage, appliedFilters, sortConfig]);

  useEffect(() => {
    loadOptions();
    // Load real users + desks for bulk-assign dropdown
    api.get('/users', { params: { limit: 500, active: true } })
      .then(r => setAllUsers(r.data.users || []))
      .catch(() => {});
    api.get('/desks')
      .then(r => setAssignDeskOpts(r.data || []))
      .catch(() => {});
    // Load all teams upfront — filtered by desk when desk is selected
    api.get('/teams')
      .then(r => setAssignTeamOpts(r.data || []))
      .catch(() => {});
  }, []);

  // Cascade: when assign desk changes → filter teams, reset team selection
  useEffect(() => {
    setAssignTeam('');
    setModalValue('');
    if (!assignDesk) {
      // No desk selected → reload all teams
      api.get('/teams').then(r => setAssignTeamOpts(r.data || [])).catch(() => {});
      return;
    }
    const desk = assignDeskOpts.find(d => d.name === assignDesk);
    if (!desk) return;
    api.get('/teams', { params: { desk_id: desk.id } })
      .then(r => setAssignTeamOpts(r.data || []))
      .catch(() => {});
  }, [assignDesk]); // eslint-disable-line

  // Reset user selection when team changes
  useEffect(() => { setModalValue(''); }, [assignTeam]);

  useEffect(() => {
    if (!showNewClient) return;
    // Load ALL active users — any role can be assigned as an agent
    api.get('/users', { params: { limit: 200, active: true } })
      .then(r => setAllAgentOpts(r.data.users || []))
      .catch(() => setAllAgentOpts([]));
  }, [showNewClient]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (actionsRef.current && !actionsRef.current.contains(event.target)) setShowActions(false);
      if (metricsRef.current && !metricsRef.current.contains(event.target)) setShowMetrics(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      userSettingsService.get(CLIENT_COLUMNS_SETTING_KEY, null),
      userSettingsService.get(CLIENT_COLUMN_WIDTHS_SETTING_KEY, null),
      userSettingsService.get(CLIENT_LIST_STATE_SETTING_KEY, {}),
    ]).then(([columns, savedColumnWidths, listState]) => {
      if (cancelled) return;

      if (columns) setVisibleColumns(toVisibleColumnSet(columns));
      const nextColumnWidths = sanitizeColumnWidths(savedColumnWidths);
      columnWidthsRef.current = nextColumnWidths;
      setColumnWidths(nextColumnWidths);
      if (listState && typeof listState === 'object') {
        setCurrentPage(sanitizePage(listState.currentPage));
        setItemsPerPage(sanitizePageSize(listState.itemsPerPage));
        setFilters(sanitizeFilters(listState.filters || listState.appliedFilters));
        setAppliedFilters(sanitizeFilters(listState.appliedFilters));
        setSortConfig(sanitizeSortConfig(listState.sortConfig));
      }
      setClientPrefsLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!clientPrefsLoaded) return;
    userSettingsService.set(CLIENT_COLUMNS_SETTING_KEY, [...visibleColumns]).catch(() => {});
  }, [clientPrefsLoaded, visibleColumns]);

  const persistVisibleColumns = (nextColumns) => {
    if (!clientPrefsLoaded) return;
    const value = [...nextColumns];
    userSettingsService.set(CLIENT_COLUMNS_SETTING_KEY, value).catch(() => {});
  };

  const persistColumnWidths = (nextWidths) => {
    if (!clientPrefsLoaded) return;
    userSettingsService.set(CLIENT_COLUMN_WIDTHS_SETTING_KEY, nextWidths).catch(() => {});
  };

  const setColumnWidth = (key, width, persist = false) => {
    const nextWidth = Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, Math.round(width)));
    const nextWidths = { ...columnWidthsRef.current, [key]: nextWidth };
    columnWidthsRef.current = nextWidths;
    setColumnWidths(nextWidths);
    if (persist) persistColumnWidths(nextWidths);
  };

  const startColumnResize = (event, key) => {
    event.preventDefault();
    event.stopPropagation();
    resizeCleanupRef.current?.();

    const startX = event.clientX;
    const startWidth = columnWidthsRef.current[key] || DEFAULT_COLUMN_WIDTHS[key];
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handlePointerMove = (moveEvent) => {
      setColumnWidth(key, startWidth + moveEvent.clientX - startX);
    };
    const cleanup = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('pointercancel', handlePointerUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      resizeCleanupRef.current = null;
    };
    const handlePointerUp = () => {
      cleanup();
      persistColumnWidths(columnWidthsRef.current);
    };

    resizeCleanupRef.current = cleanup;
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('pointercancel', handlePointerUp);
  };

  const handleColumnResizeKeyDown = (event, key) => {
    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    setColumnWidth(
      key,
      (columnWidthsRef.current[key] || DEFAULT_COLUMN_WIDTHS[key]) + direction * 10,
      true,
    );
  };

  useEffect(() => () => resizeCleanupRef.current?.(), []);

  const persistListState = (overrides = {}) => {
    if (!clientPrefsLoaded) return;
    const value = {
      currentPage,
      itemsPerPage,
      filters,
      appliedFilters,
      sortConfig,
      ...overrides,
    };
    userSettingsService.set(CLIENT_LIST_STATE_SETTING_KEY, value).catch(() => {});
  };

  useEffect(() => {
    persistListState();
  }, [clientPrefsLoaded, currentPage, itemsPerPage, filters, appliedFilters, sortConfig]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await clientsService.getClients(currentPage, itemsPerPage, {
        ...appliedFilters,
        sortBy: sortConfig.key,
        sortDir: sortConfig.direction,
      });
      setClients(data.clients);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [agents, offices, countries, statuses, languages, desks, teams, affiliates] = await Promise.all([
        clientsService.getAgents(),
        clientsService.getOffices(),
        clientsService.getCountries(),
        clientsService.getStatuses(),
        clientsService.getLanguages(),
        clientsService.getDesks(),
        clientsService.getTeams(),
        clientsService.getAffiliateOptions(),
      ]);
      setOptions({ agents, offices, countries, statuses, languages, desks, teams, affiliates });
      // Build color map from [{name, color}] response
      const cmap = {};
      statuses.forEach(s => {
        if (s && typeof s === 'object') cmap[s.name] = STATUS_PALETTE[s.color] || STATUS_PALETTE.gray;
      });
      setStatusColorMap(cmap);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const handleApplyFilters = () => {
    const nextAppliedFilters = { ...filters };
    setAppliedFilters(nextAppliedFilters);
    setCurrentPage(1);
    persistListState({ appliedFilters: nextAppliedFilters, currentPage: 1 });
  };

  const handleResetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setCurrentPage(1);
    persistListState({ filters: EMPTY_FILTERS, appliedFilters: EMPTY_FILTERS, currentPage: 1 });
  };

  // Top-bar search: update the field immediately, apply after a short debounce
  // so the list refreshes as the user types without needing the Apply button.
  const applySearch = (value) => {
    setAppliedFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  };
  const handleSearchChange = (value) => {
    setFilters(f => ({ ...f, search: value }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => applySearch(value), 400);
  };
  const handleSearchSubmit = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    applySearch(filters.search);
  };

  useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  const fetchDuplicates = async () => {
    setDuplicatesLoading(true);
    setDuplicatesSearched(true);
    setMergeReview(null);
    setMergeFieldSources({});
    try {
      const data = await clientsService.getDuplicates(duplicateCriterion);
      setDuplicates(data.groups || []);
    } catch (e) {
      toast.error('Failed to load duplicates: ' + (e.response?.data?.detail || e.message));
      setDuplicates([]);
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const openDuplicates = () => {
    setShowDuplicates(true);
    setDuplicateCriterion('name');
    setDuplicatesSearched(false);
    setDuplicates([]);
    setMergeReview(null);
    setMergeFieldSources({});
  };

  const selectDuplicateCriterion = (criterion) => {
    setDuplicateCriterion(criterion);
    setDuplicatesSearched(false);
    setDuplicates([]);
    setMergeReview(null);
    setMergeFieldSources({});
  };

  const openDuplicateClient = (clientId) => {
    setShowDuplicates(false);
    persistListState();
    navigate(`/clients/${clientId}`);
  };

  // Profile fields eligible for value selection during merge review.
  const mergeProfileFields = [
    'firstName', 'lastName', 'email', 'phone', 'mobile', 'birthdate', 'gender', 'sin', 'passport',
    'office', 'department', 'agent', 'desk', 'team', 'status',
    'salesAgent', 'salesStatus', 'salesDesk', 'salesTeam',
    'retAgent', 'retStatus', 'retDesk', 'retTeam',
    'language', 'country', 'address', 'city', 'state', 'credits',
    'source', 'referral', 'funnel', 'affiliate', 'registrationIp', 'ext_id',
  ];

  const mergeValue = (client, field) => {
    const value = client?.[field];
    return value === null || value === undefined ? '' : String(value).trim();
  };

  const getConflictingMergeFields = (groupClients) => mergeProfileFields.filter(field => {
    const values = new Set(groupClients.map(client => mergeValue(client, field)).filter(Boolean));
    return values.size > 1;
  });

  const defaultMergeFieldSources = (groupClients, primaryId) => Object.fromEntries(
    getConflictingMergeFields(groupClients).map(field => {
      const primary = groupClients.find(client => client.id === primaryId);
      const source = mergeValue(primary, field)
        ? primary
        : groupClients.find(client => mergeValue(client, field));
      return [field, source?.id ?? primaryId];
    })
  );

  const openMergeReview = async (groupKey, groupClients) => {
    const primaryId = groupClients[0]?.id;
    setMergeReview({ groupKey, clients: groupClients, primaryId });
    setMergeFieldSources({});
    setMergeReviewLoading(true);
    try {
      const data = await clientsService.getMergePreview(groupClients.map(client => client.id));
      const clientsWithDetails = data.clients.map(details => {
        const brief = groupClients.find(client => client.id === details.id);
        return {
          ...details,
          name: [details.firstName, details.lastName].filter(Boolean).join(' ') || brief?.name || '-',
        };
      });
      setMergeReview({ groupKey, clients: clientsWithDetails, primaryId });
      setMergeFieldSources(defaultMergeFieldSources(clientsWithDetails, primaryId));
    } catch (error) {
      toast.error(t('clients.mergeDetailsFailed') + ': ' + (error.response?.data?.detail || error.message));
      setMergeReview(null);
    } finally {
      setMergeReviewLoading(false);
    }
  };

  const changeMergePrimary = (primaryId) => {
    setMergeReview(prev => prev ? { ...prev, primaryId } : prev);
    setMergeFieldSources(defaultMergeFieldSources(mergeReview.clients, primaryId));
  };

  // Merge the complete duplicate group atomically, using the reviewed values.
  const doMerge = async () => {
    if (!mergeReview) return;
    setMergeLoading(true);
    try {
      const secondaryIds = mergeReview.clients
        .filter(client => client.id !== mergeReview.primaryId)
        .map(client => client.id);
      await clientsService.mergeClients(mergeReview.primaryId, secondaryIds, mergeFieldSources);
      toast.success(t('clients.mergeDone'));
      setMergeReview(null);
      setMergeFieldSources({});
      await fetchDuplicates();
      loadClients();
    } catch (e) {
      toast.error('Merge failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setMergeLoading(false);
    }
  };

  const duplicateCriteria = [
    { key: 'name', label: t('clients.duplicateName'), description: t('clients.duplicateNameHint'), Icon: User },
    { key: 'phone', label: t('clients.duplicatePhone'), description: t('clients.duplicatePhoneHint'), Icon: Phone },
    { key: 'email', label: t('clients.duplicateEmail'), description: t('clients.duplicateEmailHint'), Icon: Mail },
  ];

  const dupTypeMeta = {
    email: { label: t('clients.dupByEmail'), Icon: Mail },
    phone: { label: t('clients.dupByPhone'), Icon: Phone },
    name:  { label: t('clients.dupByName'),  Icon: User },
  };

  const activeFilterCount = Object.values(appliedFilters).filter(v => Array.isArray(v) ? v.length > 0 : v !== '').length;

  // Agent dropdown cascades from the selected Department / Desk / Team.
  // Built from real users (which carry department/desk/team) rather than the
  // flat distinct-name list, so the choices narrow with the other filters.
  const filterAgentOptions = allUsers.filter(u => {
    if (filters.department && normalizeDepartment(u.department) !== normalizeDepartment(filters.department)) return false;
    if (filters.desk.length && !filters.desk.includes(u.desk)) return false;
    if (filters.team.length && !filters.team.includes(u.team)) return false;
    return true;
  });

  // Team dropdown cascades from the selected Department / Desk.
  const filterTeamOptions = options.teams.filter(tm => {
    if (filters.desk.length && !filters.desk.includes(tm.deskName || tm.desk || '')) return false;
    if (filters.department && normalizeDepartment(tm.department) !== normalizeDepartment(filters.department)) return false;
    return true;
  });

  const handleSort = (key) => {
    const nextSortConfig = {
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    };
    setCurrentPage(1);
    setSortConfig(nextSortConfig);
    persistListState({ sortConfig: nextSortConfig, currentPage: 1 });
  };

  const openClient = (clientId) => {
    persistListState();
    navigate(`/clients/${clientId}`);
  };

  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      persistVisibleColumns(next);
      return next;
    });
  };

  const selectAllColumns = () => {
    const next = new Set(ALL_COLUMNS.map(c => c.key));
    setVisibleColumns(next);
    persistVisibleColumns(next);
  };
  const resetColumns = () => {
    const next = new Set(DEFAULT_VISIBLE);
    const nextWidths = { ...DEFAULT_COLUMN_WIDTHS };
    setVisibleColumns(next);
    columnWidthsRef.current = nextWidths;
    setColumnWidths(nextWidths);
    persistVisibleColumns(next);
    persistColumnWidths(nextWidths);
  };

  const handleImportPreview = async () => {
    if (!canImportClients) return;
    if (!importFile) { toast.error('Please select a file'); return; }
    setImportLoading(true);
    try {
      const result = await clientsService.previewImport(importFile);
      setImportColumns(result.columns || []);
      setImportMapping(autoMatchColumns(result.columns || []));
      setImportStep('mapping');
    } catch (e) {
      toast.error('Failed to read file: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportSubmit = async () => {
    if (!canImportClients) return;
    setImportLoading(true);
    try {
      const result = await clientsService.importClients(importFile, importMapping, importPreset);
      toast.success(`Imported ${result.created} client(s)`);
      if (result.errors?.length) {
        result.errors.slice(0, 3).forEach(e => toast.error(e));
      }
      closeImportModal();
      loadClients();
    } catch (e) {
      toast.error('Import failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImportLoading(false);
    }
  };

  const handleCreateClient = async () => {
    if (!canCreateClient) return;
    if (!newClientData.firstName || !newClientData.email) {
      toast.error('First name and email are required');
      return;
    }
    setNewClientLoading(true);
    try {
      await clientsService.createClient(newClientData);
      toast.success('Client created');
      setShowNewClient(false);
      setNewClientData({ firstName: '', lastName: '', email: '', phone: '', status: 'New', office: '', country: '', language: '', department: '', agent: '', desk: '', team: '', credits: '', affiliate: '', ext_id: '', source: '', referral: '', funnel: '', registrationIp: '' });
      loadClients();
    } catch (e) {
      toast.error('Failed to create client: ' + (e.response?.data?.detail || e.message));
    } finally {
      setNewClientLoading(false);
    }
  };

  const currentClients = clients;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedClients(currentClients.map(client => client.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (id) => {
    if (selectedClients.includes(id)) {
      setSelectedClients(selectedClients.filter(cId => cId !== id));
    } else {
      setSelectedClients([...selectedClients, id]);
    }
  };

  const openModal = (action) => {
    setShowActions(false);
    setModalValue('');
    // Reset assign cascade filters
    setAssignDesk('');
    setAssignTeam('');
    setAssignTeamOpts([]);
    setAssignStatus('');
    setActiveModal(action);
  };

  const HEAD_ROLES = new Set(getHeadRoles());

  // Filtered users for the assign dropdown:
  // team selected  → only members of that team (heads belong to desk, not team → excluded)
  // only desk      → all users in that desk (including heads)
  // nothing        → all users
  const filteredAssignUsers = (() => {
    if (assignTeam) return allUsers.filter(u => u.team === assignTeam && !HEAD_ROLES.has(u.role));
    if (assignDesk) return allUsers.filter(u => u.desk === assignDesk);
    return allUsers;
  })();

  const importTeamOptions = assignTeamOpts.filter(team => {
    const selectedDesk = importPreset.desk;
    const selectedDepartment = normalizeDepartment(importPreset.department);
    const teamDesk = team.deskName || team.desk || '';
    const teamDepartment = normalizeDepartment(team.department);

    if (selectedDesk && teamDesk !== selectedDesk) return false;
    if (selectedDepartment && teamDepartment !== selectedDepartment) return false;
    return true;
  });

  const importAgentOptions = allUsers.filter(user => {
    const selectedDepartment = normalizeDepartment(importPreset.department);

    if (selectedDepartment && normalizeDepartment(user.department) !== selectedDepartment) return false;
    if (importPreset.desk && user.desk !== importPreset.desk) return false;
    if (importPreset.team && user.team !== importPreset.team) return false;
    return true;
  });

  const handleBulkAction = async () => {
    if (selectedClients.length === 0) return;
    setActionLoading(true);
    try {
      let result;
      switch (activeModal) {
        case 'assign': {
          if (!modalValue) { toast.error('Please select an agent'); return; }
          result = await clientsService.bulkAssign(selectedClients, modalValue);
          // If a status was chosen, patch the dept-specific status for each client
          if (assignStatus) {
            const agent = allUsers.find(u => String(u.id) === String(modalValue));
            const dept  = (agent?.department || '').toLowerCase();
            const field = dept === 'retention' ? 'retStatus' : 'salesStatus';
            await Promise.all(selectedClients.map(id =>
              api.patch(`/clients/${id}/field`, { field, value: assignStatus })
            ));
          }
          break;
        }
        case 'block':
          result = await clientsService.bulkBlock(selectedClients);
          break;
        case 'unblock':
          result = await clientsService.bulkUnblock(selectedClients);
          break;
        case 'status':
          if (!modalValue) { toast.error('Please select a status'); return; }
          result = await clientsService.bulkChangeStatus(selectedClients, modalValue);
          break;
        case 'office':
          if (!modalValue) { toast.error('Please select an office'); return; }
          result = await clientsService.bulkChangeOffice(selectedClients, modalValue);
          break;
        case 'country':
          if (!modalValue) { toast.error('Please select a country'); return; }
          result = await clientsService.bulkChangeCountry(selectedClients, modalValue);
          break;
        case 'delete':
          await Promise.all(selectedClients.map(id => clientsService.deleteClient(id)));
          result = { message: `Deleted ${selectedClients.length} client(s)` };
          break;
        default:
          return;
      }
      toast.success(result.message);
      setActiveModal(null);
      setSelectedClients([]);
      loadClients();
    } catch (error) {
      toast.error('Action failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!canExportClients) return;
    try {
      const blob = await exportService.exportClients(format);
      exportService.downloadBlob(blob, `clients.${format}`);
      toast.success(`Exported to ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const btnClass = `flex items-center gap-2 px-4 py-2 border rounded-md transition-colors ${
    isDark
      ? 'bg-[#0a0e1a] border-gray-700 text-gray-300 hover:bg-[#1a1f3a]'
      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
  }`;

  const inputClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
    isDark ? 'bg-[#1a1f3a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
    isDark ? 'bg-[#1a1f3a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'
  }`;

  const filterLabelClass = `block mb-1 text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`;

  const actionItems = [
    { key: 'assign', label: t('clients.actions.assignTo'), icon: UserPlus },
    { key: 'block', label: t('clients.actions.blockLogin'), icon: Lock },
    { key: 'unblock', label: t('clients.actions.unblockLogin'), icon: Unlock },
    { key: 'status', label: t('clients.actions.changeStatus'), icon: RefreshCw },
    { key: 'office', label: t('clients.actions.changeOffice'), icon: Building },
    { key: 'country', label: t('clients.actions.changeCountry'), icon: Globe },
    ...(canDelete ? [{ key: 'delete', label: t('clients.actions.deleteClients'), icon: Trash2, danger: true }] : []),
  ];

  const getModalConfig = () => {
    switch (activeModal) {
      case 'assign':
        return {
          title: 'Assign To Agent',
          options: filteredAssignUsers.map(u => u.name),
          optionsRich: filteredAssignUsers,
          placeholder: 'Select user...',
        };
      case 'block':
        return { title: 'Block Client Login', confirm: true, message: `Block login for ${selectedClients.length} client(s)?` };
      case 'unblock':
        return { title: 'Unblock Client Login', confirm: true, message: `Unblock login for ${selectedClients.length} client(s)?` };
      case 'status':
        return { title: 'Change Status', statusGroups: true, placeholder: 'Select status...' };
      case 'office':
        return { title: 'Change Office', options: options.offices, placeholder: 'Select or enter office...', allowCustom: true };
      case 'country':
        return { title: 'Change Country', options: options.countries, placeholder: 'Select or enter country...', allowCustom: true };
      case 'delete':
        return { title: 'Delete Clients', confirm: true, danger: true, message: `Permanently delete ${selectedClients.length} client(s)? This cannot be undone.` };
      default:
        return null;
    }
  };

  const isShowingAll = itemsPerPage === 0;
  const startIndex = isShowingAll ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = isShowingAll ? total : Math.min(startIndex + itemsPerPage, total);
  const firstVisibleIndex = total > 0 ? startIndex + 1 : 0;
  const visibleColumnDefinitions = ALL_COLUMNS.filter(column => visibleColumns.has(column.key));
  const tableWidth = SELECTION_COLUMN_WIDTH + visibleColumnDefinitions.reduce(
    (sum, column) => sum + columnWidths[column.key],
    0,
  );

  if (!clientPrefsLoaded || (loading && clients.length === 0)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className={`text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>Loading...</div>
      </div>
    );
  }

  const modalConfig = getModalConfig();

  return (
    <div className="clients-page p-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <button
            className={`${btnClass} ${showFilters ? (isDark ? 'bg-[#1a1f3a] border-purple-500 text-purple-400' : 'bg-purple-50 border-purple-400 text-purple-700') : ''}`}
            onClick={() => setShowFilters(v => !v)}
          >
            <Filter size={18} />
            <span>{t('clients.filters')}</span>
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          {/* Metrics = column visibility dropdown */}
          <div className="relative" ref={metricsRef}>
            <button
              className={`${btnClass} ${showMetrics ? (isDark ? 'bg-[#1a1f3a] border-purple-500 text-purple-400' : 'bg-purple-50 border-purple-400 text-purple-700') : ''}`}
              onClick={() => setShowMetrics(v => !v)}
            >
              <MoreVertical size={18} />
              <span>{t('clients.metrics')}</span>
              <ChevronDown size={14} className={`transition-transform ${showMetrics ? 'rotate-180' : ''}`} />
            </button>

            {showMetrics && (
              <div className={`absolute top-full left-0 mt-1 rounded-lg shadow-xl border z-50 p-4 ${
                isDark ? 'bg-[#1a1f3a] border-gray-700' : 'bg-white border-gray-200'
              }`} style={{ minWidth: 480 }}>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4">
                  {ALL_COLUMNS.map(col => (
                    <label key={col.key} className={`flex items-center gap-2 cursor-pointer text-sm select-none ${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-700 hover:text-gray-900'}`}>
                      <input
                        type="checkbox"
                        checked={visibleColumns.has(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        className="w-4 h-4 rounded border text-red-500 accent-red-500"
                      />
                      {columnLabel(col.key, col.label)}
                    </label>
                  ))}
                </div>
                <div className={`flex justify-end gap-2 border-t pt-3 ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                  <button onClick={resetColumns} className={`px-4 py-1.5 text-sm border rounded-md transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {t('clients.resetAll')}
                  </button>
                  <button onClick={selectAllColumns} className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700">
                    {t('clients.selectAll')}
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Top-bar search — applies live, independent of the Filters panel */}
          <div className="relative w-56 md:w-72">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              placeholder={t('clients.searchPlaceholder')}
              value={filters.search}
              onChange={e => handleSearchChange(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSearchSubmit(); }}
              className={`w-full pl-9 pr-8 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            />
            {filters.search && (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                title={t('clients.reset')}
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
              >
                <X size={14} />
              </button>
            )}
          </div>
          {/* Duplicates finder */}
          <button onClick={openDuplicates} className={btnClass} title={t('clients.duplicatesTitle')}>
            <Copy size={18} />
            <span>{t('clients.duplicates')}</span>
          </button>
          {selectedClients.length > 0 && (
            <div className="relative" ref={actionsRef}>
              <button
                onClick={() => setShowActions(!showActions)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all font-medium text-sm ${
                  showActions
                    ? 'bg-purple-600 border border-purple-600 text-white hover:bg-purple-700'
                    : 'bg-purple-600/90 border border-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                <span>{t('clients.actionsLabel')} ({selectedClients.length})</span>
                <ChevronDown size={18} className={`transition-transform ${showActions ? 'rotate-180' : ''}`} />
              </button>
              {showActions && (
                <div className={`absolute top-full left-0 mt-1 w-56 rounded-md shadow-lg z-50 border ${
                  isDark ? 'bg-[#1a1f3a] border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  {actionItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => openModal(item.key)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                          item.danger
                            ? 'text-red-400 hover:bg-red-500/10'
                            : isDark ? 'text-gray-300 hover:bg-[#2a2f4a]' : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Icon size={16} />
                        <span>{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {(canImportClients || canExportClients || canCreateClient) && (
          <div className="flex items-center gap-3">
            {canImportClients && (
              <button onClick={() => setShowImport(true)} className={btnClass}>
                <Upload size={18} />
                <span>{t('clients.import')}</span>
              </button>
            )}
            {canExportClients && (
              <>
                <button onClick={() => handleExport('csv')} className={btnClass}>
                  <Download size={18} />
                  <span>CSV</span>
                </button>
                <button onClick={() => handleExport('xlsx')} className={btnClass}>
                  <FileSpreadsheet size={18} />
                  <span>Excel</span>
                </button>
              </>
            )}
            {canCreateClient && (
              <button
                onClick={() => setShowNewClient(true)}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                <Plus size={18} />
                <span>{t('clients.new')}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className={`mb-4 p-4 border rounded-lg ${isDark ? 'bg-[#0f1729] border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-3 mb-3">
            {/* Status — grouped by Sales / Retention */}
            <div>
              <label className={filterLabelClass}>Status</label>
              <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                {['sales','retention'].map(et => {
                  const group = options.statuses.filter(s => (s.entityType || 'sales') === et);
                  if (!group.length) return null;
                  return (
                    <optgroup key={et} label={et === 'sales' ? t('clients.departments.sales') : t('clients.departments.retention')}>
                      {group.map(s => {
                        const n = typeof s === 'object' ? s.name : s;
                        return <option key={`${et}-${n}`} value={n}>{n}</option>;
                      })}
                    </optgroup>
                  );
                })}
              </select>
            </div>
            {/* Country */}
            <div>
              <label className={filterLabelClass}>Country</label>
              <select value={filters.country} onChange={e => setFilters(f => ({ ...f, country: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                {options.countries.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Office */}
            <div>
              <label className={filterLabelClass}>Office</label>
              <select value={filters.office} onChange={e => setFilters(f => ({ ...f, office: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                {options.offices.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            {/* Department — resets dependent team/agent selections */}
            <div>
              <label className={filterLabelClass}>Department</label>
              <select
                value={filters.department}
                onChange={e => setFilters(f => ({ ...f, department: e.target.value, team: [], agent: [] }))}
                className={selectClass}
              >
                <option value="">All</option>
                <option value="sales">{t('clients.departments.sales')}</option>
                <option value="retention">{t('clients.departments.retention')}</option>
              </select>
            </div>
            {/* Desk — multi-select, resets dependent team/agent selections */}
            <div>
              <label className={filterLabelClass}>Desk</label>
              <MultiSelectDropdown
                isDark={isDark}
                searchable
                allLabel="All"
                value={filters.desk}
                onChange={vals => setFilters(f => ({ ...f, desk: vals, team: [], agent: [] }))}
                options={options.desks.map(d => ({ value: d.name, label: d.name }))}
              />
            </div>
            {/* Team — multi-select, cascades from Department / Desk, resets agent */}
            <div>
              <label className={filterLabelClass}>Team</label>
              <MultiSelectDropdown
                isDark={isDark}
                searchable
                allLabel="All"
                value={filters.team}
                onChange={vals => setFilters(f => ({ ...f, team: vals, agent: [] }))}
                options={filterTeamOptions.map(tm => ({ value: tm.name, label: tm.name }))}
              />
            </div>
            {/* Agent — multi-select, cascades from Department / Desk / Team */}
            <div>
              <label className={filterLabelClass}>Agent</label>
              <MultiSelectDropdown
                isDark={isDark}
                searchable
                allLabel="All"
                value={filters.agent}
                onChange={vals => setFilters(f => ({ ...f, agent: vals }))}
                options={filterAgentOptions.map(u => ({ value: u.name, label: u.name }))}
              />
            </div>
            {/* Language */}
            <div>
              <label className={filterLabelClass}>Language</label>
              <select value={filters.language} onChange={e => setFilters(f => ({ ...f, language: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                {options.languages.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            {/* Affiliate */}
            <div>
              <label className={filterLabelClass}>Affiliate</label>
              <select value={filters.affiliate} onChange={e => setFilters(f => ({ ...f, affiliate: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                {options.affiliates.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            {/* Sales status */}
            <div>
              <label className={filterLabelClass}>Sales status</label>
              <select value={filters.salesStatus} onChange={e => setFilters(f => ({ ...f, salesStatus: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                {options.statuses
                  .filter(s => (s.entityType || 'sales') === 'sales')
                  .map(s => <option key={`ss-${s.name}`} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            {/* Retention status */}
            <div>
              <label className={filterLabelClass}>Retention status</label>
              <select value={filters.retStatus} onChange={e => setFilters(f => ({ ...f, retStatus: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                {options.statuses
                  .filter(s => (s.entityType || 'sales') === 'retention')
                  .map(s => <option key={`rs-${s.name}`} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            {/* FTD */}
            <div>
              <label className={filterLabelClass}>FTD</label>
              <select value={filters.ftd} onChange={e => setFilters(f => ({ ...f, ftd: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            {/* Affiliate / source / referral search */}
            <div>
              <label className={filterLabelClass}>Affiliate ID / source</label>
              <input
                type="text"
                placeholder="Affiliate ID / source"
                value={filters.refSearch}
                onChange={e => setFilters(f => ({ ...f, refSearch: e.target.value }))}
                className={inputClass}
              />
            </div>
            {/* Registration date range */}
            <div>
              <label className={filterLabelClass}>Registration date range</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  title="Registered from"
                  value={filters.regFrom}
                  onChange={e => setFilters(f => ({ ...f, regFrom: e.target.value }))}
                  className={inputClass}
                />
                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>–</span>
                <input
                  type="date"
                  title="Registered to"
                  value={filters.regTo}
                  onChange={e => setFilters(f => ({ ...f, regTo: e.target.value }))}
                  className={inputClass}
                />
              </div>
            </div>
            {/* Last modification date range */}
            <div>
              <label className={filterLabelClass}>Last modification date range</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  title="Modified from"
                  value={filters.modFrom}
                  onChange={e => setFilters(f => ({ ...f, modFrom: e.target.value }))}
                  className={inputClass}
                />
                <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>–</span>
                <input
                  type="date"
                  title="Modified to"
                  value={filters.modTo}
                  onChange={e => setFilters(f => ({ ...f, modTo: e.target.value }))}
                  className={inputClass}
                />
              </div>
            </div>
            {/* Active */}
            <div>
              <label className={filterLabelClass}>Activity</label>
              <select value={filters.active} onChange={e => setFilters(f => ({ ...f, active: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                <option value="true">{t('clients.activeOnly')}</option>
                <option value="false">{t('clients.inactiveOnly')}</option>
              </select>
            </div>
            {/* Update status */}
            <div>
              <label className={filterLabelClass}>Update state</label>
              <select value={filters.needsUpdate} onChange={e => setFilters(f => ({ ...f, needsUpdate: e.target.value }))} className={selectClass}>
                <option value="">All</option>
                <option value="true">3+ days without update</option>
                <option value="false">Actual</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleApplyFilters} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm">
              {t('clients.applyFilters')}
            </button>
            <button onClick={handleResetFilters} className={`px-4 py-2 border rounded-md text-sm ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#1a1f3a]' : 'border-gray-300 text-gray-700 hover:bg-white'}`}>
              {t('clients.reset')}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className={`border rounded-lg overflow-hidden ${isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="clients-table-scroll overflow-auto">
          <table className="min-w-full table-fixed text-sm" style={{ width: `${tableWidth}px` }}>
            <colgroup>
              <col style={{ width: `${SELECTION_COLUMN_WIDTH}px` }} />
              {visibleColumnDefinitions.map(column => (
                <col key={column.key} style={{ width: `${columnWidths[column.key]}px` }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr className={`border-b ${isDark ? 'bg-[#1a1f3a] border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                <th className="px-4 py-3" style={{ width: `${SELECTION_COLUMN_WIDTH}px` }}>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedClients.length === currentClients.length && currentClients.length > 0}
                    className={`w-4 h-4 rounded border text-purple-500 focus:ring-purple-500 focus:ring-offset-0 ${
                      isDark ? 'border-gray-700 bg-[#0a0e1a]' : 'border-gray-300 bg-white'
                    }`}
                  />
                </th>
                {visibleColumnDefinitions.map(col => (
                  <th
                    key={col.key}
                    className={`relative overflow-hidden text-left px-4 py-3 font-medium whitespace-nowrap ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                    style={{ width: `${columnWidths[col.key]}px` }}
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className={`group flex w-full items-center gap-1.5 overflow-hidden pr-2 text-left uppercase transition-colors ${
                        isDark ? 'hover:text-white' : 'hover:text-gray-950'
                      }`}
                      title={`Sort by ${columnLabel(col.key, col.label)}`}
                    >
                      <span className="truncate">{columnLabel(col.key, col.label)}</span>
                      <ChevronDown
                        size={12}
                        className={`flex-shrink-0 transition-all ${
                          sortConfig.key === col.key
                            ? (sortConfig.direction === 'asc' ? 'rotate-180 opacity-100' : 'opacity-100')
                            : 'opacity-0 group-hover:opacity-40'
                        }`}
                      />
                    </button>
                    <button
                      type="button"
                      aria-label={`Resize ${columnLabel(col.key, col.label)} column`}
                      title={`Resize ${columnLabel(col.key, col.label)} column`}
                      onClick={event => event.stopPropagation()}
                      onPointerDown={event => startColumnResize(event, col.key)}
                      onKeyDown={event => handleColumnResizeKeyDown(event, col.key)}
                      className={`group/resize absolute right-0 top-0 h-full w-2 cursor-col-resize touch-none outline-none ${
                        isDark ? 'hover:bg-purple-400/10 focus:bg-purple-400/10' : 'hover:bg-purple-500/10 focus:bg-purple-500/10'
                      }`}
                    >
                      <span className={`absolute right-0 top-1/4 h-1/2 w-px transition-colors ${
                        isDark
                          ? 'bg-gray-700 group-hover/resize:bg-purple-400 group-focus/resize:bg-purple-400'
                          : 'bg-gray-300 group-hover/resize:bg-purple-500 group-focus/resize:bg-purple-500'
                      }`} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentClients.map((client) => {
                const highlightUpdate = needsClientUpdate(client);
                return (
                <tr
                  key={client.id}
                  onClick={() => openClient(client.id)}
                  title={clientUpdateTitle(client)}
                  className={`border-b transition-colors cursor-pointer ${
                    isDark
                      ? 'border-gray-800/50 hover:bg-[#1a1f3a]/60'
                      : 'border-gray-100 hover:bg-blue-50'
                  } ${
                    highlightUpdate
                      ? (isDark ? 'bg-amber-400/[0.055] hover:bg-amber-400/[0.085]' : 'bg-amber-50/55 hover:bg-amber-100/55')
                      : ''
                  } ${selectedClients.includes(client.id) ? (isDark ? 'bg-purple-900/20' : 'bg-purple-50') : ''}`}
                >
                  <td className={`relative px-4 py-3 ${highlightUpdate ? 'before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-r-full before:bg-amber-400/80' : ''}`} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.id)}
                      onChange={() => handleSelectClient(client.id)}
                      className={`w-4 h-4 rounded border text-purple-500 focus:ring-purple-500 focus:ring-offset-0 ${
                        isDark ? 'border-gray-700 bg-[#0a0e1a]' : 'border-gray-300 bg-white'
                      }`}
                    />
                  </td>
                  {visibleColumnDefinitions.map(col => (
                    <td
                      key={col.key}
                      className="overflow-hidden text-ellipsis whitespace-nowrap px-4 py-3"
                      style={{ width: `${columnWidths[col.key]}px` }}
                    >
                      {col.key === 'id' && (
                        <span className={isDark ? 'text-gray-300' : 'text-gray-900'}>#{client.id}</span>
                      )}
                      {col.key === 'active' && (
                        <span className={`text-xs font-medium ${client.active ? 'text-green-500' : 'text-red-500'}`}>
                          {client.active ? t('clients.yes') : t('clients.no')}
                        </span>
                      )}
                      {col.key === 'department' && (
                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                          {departmentLabel(client.department)}
                        </span>
                      )}
                      {col.key === 'client' && (
                        <span className={isDark ? 'text-gray-300' : 'text-gray-900'}>
                          {[client.firstName, client.lastName].filter(Boolean).join(' ') || '-'}
                        </span>
                      )}
                      {col.key === 'salesStatus' && (() => {
                        const dept = (client.department || '').toLowerCase();
                        const sv = client.salesStatus || (dept !== 'retention' ? client.status : '');
                        return sv
                          ? <span className={`px-2 py-1 rounded text-xs font-medium ${statusColorMap[sv] || STATUS_PALETTE.gray}`}>{sv}</span>
                          : <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>—</span>;
                      })()}
                      {col.key === 'retStatus' && (() => {
                        const dept = (client.department || '').toLowerCase();
                        const rv = client.retStatus || (dept === 'retention' ? client.status : '');
                        return rv
                          ? <span className={`px-2 py-1 rounded text-xs font-medium ${statusColorMap[rv] || STATUS_PALETTE.gray}`}>{rv}</span>
                          : <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>—</span>;
                      })()}
                      {['lastCommentDate', 'registeredAt'].includes(col.key) && (
                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                          {formatClientTableDate(client[col.key])}
                        </span>
                      )}
                      {!['id','active','client','salesStatus','retStatus','department','lastCommentDate','registeredAt'].includes(col.key) && (
                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                          {client[col.key] ?? '-'}
                        </span>
                      )}
                    </td>
                  ))}
                </tr>
                );
              })}
              {currentClients.length === 0 && !loading && (
                <tr>
                  <td colSpan={visibleColumnDefinitions.length + 1} className={`px-4 py-8 text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {t('clients.noClientsFound')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className={`flex items-center justify-between px-6 py-4 border-t ${
          isDark ? 'bg-[#0a0e1a] border-gray-800' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('clients.showing')} {firstVisibleIndex} {t('clients.to')} {endIndex} {t('clients.of')} {total} {t('clients.entries')}
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                const nextItemsPerPage = Number(e.target.value);
                setItemsPerPage(nextItemsPerPage);
                setCurrentPage(1);
                persistListState({ itemsPerPage: nextItemsPerPage, currentPage: 1 });
              }}
              className={`ml-2 border rounded px-2 py-1 text-sm focus:outline-none focus:border-purple-500 ${
                isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={1000}>1000</option>
              <option value={0}>All</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className={`text-sm mr-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('clients.page')} {currentPage} {t('clients.of')} {totalPages}
            </span>
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className={`p-2 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}><ChevronsLeft size={16} /></button>
            <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className={`p-2 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}><ChevronLeft size={16} /></button>
            <button className="px-4 py-2 bg-purple-600 text-white rounded font-medium">{currentPage}</button>
            {currentPage < totalPages && (
              <button onClick={() => setCurrentPage(currentPage + 1)} className={`px-4 py-2 border rounded transition-colors ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{currentPage + 1}</button>
            )}
            <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className={`p-2 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}><ChevronRight size={16} /></button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className={`p-2 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDark ? 'bg-[#1a1f3a] border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}><ChevronsRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* ───── Bulk Action Modal ───── */}
      {activeModal && modalConfig && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-md p-6 rounded-lg ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{modalConfig.title}</h2>
              <button onClick={() => setActiveModal(null)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}><X size={20} /></button>
            </div>
            <div className="mb-4">
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {modalConfig.confirm ? modalConfig.message : `Apply to ${selectedClients.length} selected client(s)`}
              </p>
              {!modalConfig.confirm && (
                <>
                  {/* ── Assign: Desk → Team → User cascade ── */}
                  {activeModal === 'assign' ? (
                    <div className="space-y-3">
                      {/* Desk filter */}
                      <div>
                        <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Filter by Desk <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>(optional)</span>
                        </label>
                        <select
                          value={assignDesk}
                          onChange={e => setAssignDesk(e.target.value)}
                          className={selectClass}
                        >
                          <option value="">— All Desks —</option>
                          {assignDeskOpts.map(d => (
                            <option key={d.id} value={d.name}>{d.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Team filter (cascades from desk) */}
                      <div>
                        <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Filter by Team <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>(optional)</span>
                        </label>
                        <select
                          value={assignTeam}
                          onChange={e => setAssignTeam(e.target.value)}
                          className={selectClass}
                        >
                          <option value="">
                            {assignDesk ? '— All Teams in Desk —' : '— All Teams —'}
                          </option>
                          {assignTeamOpts.map(t => (
                            <option key={t.id} value={t.name}>{t.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* User select filtered by desk/team */}
                      <div>
                        <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          User *{' '}
                          <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>
                            ({filteredAssignUsers.length} available)
                          </span>
                        </label>
                        <select
                          value={modalValue}
                          onChange={e => { setModalValue(e.target.value); setAssignStatus(''); }}
                          className={selectClass}
                        >
                          <option value="">— Select User —</option>
                          {filteredAssignUsers.map(u => (
                            <option key={u.id} value={u.id}>{u.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Status for the assigned dept */}
                      {modalValue && (() => {
                        const agent = allUsers.find(u => String(u.id) === String(modalValue));
                        const dept  = (agent?.department || 'sales').toLowerCase();
                        const deptStatuses = options.statuses.filter(s => (s.entityType || 'sales').toLowerCase() === dept);
                        const deptLabel = dept === 'retention' ? 'Retention' : 'Sales';
                        const deptColor = dept === 'retention' ? (isDark ? 'text-cyan-400' : 'text-cyan-600') : (isDark ? 'text-blue-400' : 'text-blue-600');
                        return (
                          <div>
                            <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              Status{' '}
                              <span className={`font-semibold ${deptColor}`}>({deptLabel})</span>{' '}
                              <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>(optional)</span>
                            </label>
                            <select
                              value={assignStatus}
                              onChange={e => setAssignStatus(e.target.value)}
                              className={selectClass}
                            >
                              <option value="">— Keep current status —</option>
                              {deptStatuses.map(s => {
                                const n = typeof s === 'object' ? s.name : s;
                                return <option key={n} value={n}>{n}</option>;
                              })}
                            </select>
                          </div>
                        );
                      })()}
                    </div>
                  ) : modalConfig.statusGroups ? (
                    /* Grouped status select: Sales / Retention */
                    <select value={modalValue} onChange={e => setModalValue(e.target.value)} className={inputClass}>
                      <option value="">{modalConfig.placeholder}</option>
                      {['sales', 'retention'].map(et => {
                        const group = options.statuses.filter(s => (s.entityType || 'sales') === et);
                        if (!group.length) return null;
                        return (
                          <optgroup key={et} label={et === 'sales' ? 'Sales' : 'Retention'}>
                            {group.map(s => {
                              const n = typeof s === 'object' ? s.name : s;
                              return <option key={`${et}-${n}`} value={n}>{n}</option>;
                            })}
                          </optgroup>
                        );
                      })}
                    </select>
                  ) : modalConfig.allowCustom ? (
                    <input type="text" value={modalValue} onChange={(e) => setModalValue(e.target.value)} placeholder={modalConfig.placeholder} list={`${activeModal}-options`} className={inputClass} />
                  ) : (
                    <select value={modalValue} onChange={(e) => setModalValue(e.target.value)} className={inputClass}>
                      <option value="">{modalConfig.placeholder}</option>
                      {modalConfig.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  )}
                  {modalConfig.allowCustom && activeModal !== 'assign' && (
                    <datalist id={`${activeModal}-options`}>
                      {modalConfig.options?.map((opt) => <option key={opt} value={opt} />)}
                    </datalist>
                  )}
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setActiveModal(null)} className={`flex-1 py-2 border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
              <button onClick={handleBulkAction} disabled={actionLoading} className={`flex-1 py-2 text-white rounded-md disabled:opacity-50 transition-colors ${modalConfig?.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'}`}>
                {actionLoading ? 'Processing...' : modalConfig?.danger ? 'Delete' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* ───── Import Modal ───── */}
      {canImportClients && showImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full rounded-lg ${importStep === 'mapping' ? 'max-w-2xl' : 'max-w-md'} ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>

            {/* Header */}
            <div className="flex items-center justify-between p-6 pb-4">
              <div className="flex items-center gap-3">
                <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Import Clients</h2>
                {importStep === 'mapping' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-purple-900/50 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                    Step 2: Map columns
                  </span>
                )}
              </div>
              <button onClick={closeImportModal} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}><X size={20} /></button>
            </div>

            <div className="px-6 pb-6">
              {importStep === 'idle' ? (
                <>
                  <p className={`text-xs mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Upload a <strong>CSV</strong> or <strong>Excel (.xlsx)</strong> file — you'll map the columns on the next step.
                  </p>
                  {/* Drop zone */}
                  <div
                    onClick={() => importFileRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setImportDragging(true); }}
                    onDragEnter={e => { e.preventDefault(); setImportDragging(true); }}
                    onDragLeave={e => { e.preventDefault(); setImportDragging(false); }}
                    onDrop={e => {
                      e.preventDefault(); setImportDragging(false);
                      const f = e.dataTransfer.files[0];
                      if (f) setImportFile(f);
                    }}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      importDragging ? 'border-purple-500 bg-purple-500/10'
                        : isDark ? 'border-gray-600 hover:border-purple-500 text-gray-400'
                        : 'border-gray-300 hover:border-purple-400 text-gray-500'
                    }`}
                  >
                    <Upload size={32} className={`mx-auto mb-2 transition-opacity ${importDragging ? 'opacity-100 text-purple-500' : 'opacity-50'}`} />
                    {importFile
                      ? <p className={`text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{importFile.name}</p>
                      : importDragging
                        ? <p className="text-sm font-medium text-purple-500">Drop file here</p>
                        : <p className="text-sm">Drag & drop or click to select CSV / Excel file</p>
                    }
                    <input ref={importFileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
                      onChange={e => setImportFile(e.target.files[0] || null)} />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button onClick={closeImportModal} className={`flex-1 py-2 border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
                    <button onClick={handleImportPreview} disabled={importLoading || !importFile} className="flex-1 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors">
                      {importLoading ? 'Reading...' : 'Next →'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Match each CRM field to a column from <strong>{importFile?.name}</strong>. Fields marked <span className="text-red-500">*</span> are required.
                  </p>

                  {/* Column mapping table */}
                  <div className={`rounded-lg border overflow-hidden mb-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`grid grid-cols-2 text-xs font-semibold px-3 py-2 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                      <span>CRM Field</span>
                      <span>File Column</span>
                    </div>
                    <div className={`divide-y max-h-56 overflow-y-auto ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                      {IMPORT_FIELDS.map(f => (
                        <div key={f.key} className={`grid grid-cols-2 items-center px-3 py-1.5 ${isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}>
                          <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                          </span>
                          <select
                            value={importMapping[f.key] || ''}
                            onChange={e => setImportMapping(m => ({ ...m, [f.key]: e.target.value }))}
                            className={`text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-purple-500 ${
                              isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                            } ${f.required && !importMapping[f.key] ? 'border-red-400' : ''}`}
                          >
                            <option value="">— Skip —</option>
                            {importColumns.map(c => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Assign to (CRM) */}
                  <div className={`rounded-lg border p-3 mb-4 ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Assign to</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">

                      {/* Agent */}
                      <div>
                        <label className={`text-xs mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Agent</label>
                        <select
                          value={importPreset.agent_id}
                          onChange={e => {
                            const u = allUsers.find(u => String(u.id) === e.target.value);
                            setImportPreset(p => ({
                              ...p,
                              agent_id: e.target.value,
                              department: u?.department || p.department,
                              desk: u?.desk || p.desk,
                              team: u?.team || p.team,
                            }));
                          }}
                          className={`w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                          <option value="">— Skip —</option>
                          {importAgentOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </div>

                      {/* Department */}
                      <div>
                        <label className={`text-xs mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Department</label>
                        <select
                          value={importPreset.department}
                          onChange={e => setImportPreset(p => ({ ...p, agent_id: '', department: e.target.value, team: '' }))}
                          className={`w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                          <option value="">— Skip —</option>
                          <option value="sales">Sales</option>
                          <option value="retention">Retention</option>
                        </select>
                      </div>

                      {/* Desk */}
                      <div>
                        <label className={`text-xs mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Desk</label>
                        <select
                          value={importPreset.desk}
                          onChange={e => setImportPreset(p => ({ ...p, agent_id: '', desk: e.target.value, team: '' }))}
                          className={`w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                          <option value="">— Skip —</option>
                          {assignDeskOpts.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                        </select>
                      </div>

                      {/* Team */}
                      <div>
                        <label className={`text-xs mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Team</label>
                        <select
                          value={importPreset.team}
                          onChange={e => setImportPreset(p => ({ ...p, agent_id: '', team: e.target.value }))}
                          className={`w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#0a0e1a] border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                          <option value="">— Skip —</option>
                          {importTeamOptions.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </select>
                      </div>

                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => setImportStep('idle')} className={`px-4 py-2 border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>← Back</button>
                    <button
                      onClick={handleImportSubmit}
                      disabled={importLoading || !importMapping.email || !importMapping.first_name}
                      className="flex-1 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors"
                    >
                      {importLoading ? 'Importing...' : `Import from "${importFile?.name}"`}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ───── New Client Modal ───── */}
      {canCreateClient && showNewClient && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-lg p-6 rounded-lg max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#1a1f3a]' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>New Client</h2>
              <button onClick={() => setShowNewClient(false)} className={isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'First Name *', field: 'firstName', type: 'text' },
                { label: 'Last Name', field: 'lastName', type: 'text' },
                { label: 'Email *', field: 'email', type: 'email' },
                { label: 'Phone', field: 'phone', type: 'text' },
                { label: 'Country', field: 'country', type: 'text' },
                { label: 'Language', field: 'language', type: 'text' },
                { label: 'Credits', field: 'credits', type: 'text' },
                { label: 'Affiliate', field: 'affiliate', type: 'text' },
                { label: 'Affiliate ID', field: 'ext_id', type: 'text' },
                { label: 'Source', field: 'source', type: 'text' },
                { label: 'Referral', field: 'referral', type: 'text' },
                { label: 'Funnel', field: 'funnel', type: 'text' },
                { label: 'Reg. IP', field: 'registrationIp', type: 'text' },
              ].map(({ label, field, type }) => (
                <div key={field}>
                  <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{label}</label>
                  <input
                    type={type}
                    value={newClientData[field]}
                    onChange={e => setNewClientData(d => ({ ...d, [field]: e.target.value }))}
                    className={inputClass}
                  />
                </div>
              ))}
              {/* Agent dropdown — all users, desk/office auto-derived */}
              <div className="col-span-2">
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Assign to Agent</label>
                <select
                  value={newClientData.agent}
                  onChange={e => {
                    const aName = e.target.value;
                    const a = allAgentOpts.find(x => x.name === aName);
                    setNewClientData(d => ({ ...d, agent: aName, office: a?.office || '', department: a?.department || '', desk: a?.desk || '', team: a?.team || '' }));
                  }}
                  className={selectClass}
                >
                  <option value="">— Select User —</option>
                  {allAgentOpts.map(a => (
                    <option key={a.id} value={a.name}>{a.name}</option>
                  ))}
                </select>
                {newClientData.agent && (
                  <div className={`flex gap-4 mt-1.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {newClientData.department && (
                      <span>Dept: <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{newClientData.department}</span></span>
                    )}
                    {newClientData.desk && (
                      <span>Desk: <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{newClientData.desk}</span></span>
                    )}
                    {newClientData.office && (
                      <span>Office: <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{newClientData.office}</span></span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className={`block text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Status</label>
                <select value={newClientData.status} onChange={e => setNewClientData(d => ({ ...d, status: e.target.value }))} className={selectClass}>
                  {options.statuses.length ? (
                    ['sales', 'retention'].map(et => {
                      const group = options.statuses.filter(s => (s.entityType || 'sales') === et);
                      if (!group.length) return null;
                      return (
                        <optgroup key={et} label={et === 'sales' ? 'Sales' : 'Retention'}>
                          {group.map(s => {
                            const n = typeof s === 'object' ? s.name : s;
                            return <option key={`${et}-${n}`} value={n}>{n}</option>;
                          })}
                        </optgroup>
                      );
                    })
                  ) : (
                    ['New','FTD','Test','Imported','Follow UP','Fraud'].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))
                  )}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowNewClient(false)} className={`flex-1 py-2 border rounded-md transition-colors ${isDark ? 'border-gray-700 text-gray-300 hover:bg-[#2a2f4a]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>Cancel</button>
              <button onClick={handleCreateClient} disabled={newClientLoading} className="flex-1 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition-colors">
                {newClientLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───── Duplicates Modal ───── */}
      {showDuplicates && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" role="presentation">
          <div
            className={`w-full ${mergeReview ? 'max-w-5xl' : 'max-w-3xl'} max-h-[88vh] flex flex-col overflow-hidden rounded-xl border shadow-2xl ${isDark ? 'bg-[#151a31] border-gray-700/80' : 'bg-white border-gray-200'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="duplicates-modal-title"
          >
            <div className={`flex items-start justify-between gap-4 px-6 py-5 border-b ${isDark ? 'border-gray-700/80' : 'border-gray-200'}`}>
              <div className="flex min-w-0 items-start gap-3">
                {mergeReview && (
                  <button
                    type="button"
                    onClick={() => setMergeReview(null)}
                    disabled={mergeLoading || mergeReviewLoading}
                    className={`mt-0.5 shrink-0 p-1 rounded-md transition-colors disabled:opacity-50 ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                    title={t('clients.mergeBack')}
                    aria-label={t('clients.mergeBack')}
                  >
                    <ChevronLeft size={19} />
                  </button>
                )}
                <div className="min-w-0">
                  <h2 id="duplicates-modal-title" className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {mergeReview ? t('clients.mergeReviewTitle') : t('clients.duplicatesTitle')}
                  </h2>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {mergeReview ? t('clients.mergeReviewHint') : t('clients.duplicatesHint')}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { setShowDuplicates(false); setMergeReview(null); }}
                className={`shrink-0 p-1.5 rounded-md transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}
                title={t('clients.closeDuplicates')}
                aria-label={t('clients.closeDuplicates')}
              >
                <X size={19} />
              </button>
            </div>
            {mergeReview ? (
              <div className="min-h-0 flex flex-1 flex-col">
                {mergeReviewLoading ? (
                  <div className={`flex min-h-[18rem] flex-1 items-center justify-center gap-2 text-sm ${isDark ? 'bg-[#11162b] text-gray-400' : 'bg-gray-50/70 text-gray-500'}`}>
                    <RefreshCw size={17} className="animate-spin" />
                    {t('clients.mergeDetailsLoading')}
                  </div>
                ) : (
                  <>
                <div className={`min-h-0 flex-1 overflow-y-auto px-6 py-5 ${isDark ? 'bg-[#11162b]' : 'bg-gray-50/70'}`}>
                  <section>
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div>
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{t('clients.mergeBaseTitle')}</h3>
                        <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('clients.mergeBaseHint')}</p>
                      </div>
                      <span className={`shrink-0 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {mergeReview.clients.length} {t('clients.mergeRecords')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {mergeReview.clients.map(client => {
                        const selected = mergeReview.primaryId === client.id;
                        return (
                          <button
                            key={client.id}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            onClick={() => changeMergePrimary(client.id)}
                            disabled={mergeLoading}
                            className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors disabled:opacity-60 ${selected
                              ? (isDark ? 'border-purple-500 bg-purple-500/10' : 'border-purple-500 bg-purple-50')
                              : (isDark ? 'border-gray-700 bg-[#171d36] hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-300')
                            }`}
                          >
                            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected ? 'border-purple-500' : (isDark ? 'border-gray-600' : 'border-gray-300')}`}>
                              {selected && <span className="h-2 w-2 rounded-full bg-purple-500" />}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2">
                                <span className={`truncate text-sm font-medium ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{client.name}</span>
                                <span className={`shrink-0 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>#{client.id}</span>
                              </span>
                              <span className={`mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span className="truncate">{client.email || '—'}</span>
                                <span className="truncate">{client.phone || client.mobile || '—'}</span>
                              </span>
                            </span>
                            {selected && (
                              <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${isDark ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>
                                {t('clients.mergeKeptId')}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className={`mt-5 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{t('clients.mergeValuesTitle')}</h3>
                    <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('clients.mergeValuesHint')}</p>
                    {getConflictingMergeFields(mergeReview.clients).length === 0 ? (
                      <div className={`mt-3 rounded-lg border px-4 py-5 text-center text-sm ${isDark ? 'border-gray-700 bg-[#171d36] text-gray-400' : 'border-gray-200 bg-white text-gray-500'}`}>
                        {t('clients.mergeNoConflicts')}
                      </div>
                    ) : (
                      <div className={`mt-3 overflow-hidden rounded-lg border ${isDark ? 'border-gray-700 bg-[#171d36]' : 'border-gray-200 bg-white'}`}>
                        {getConflictingMergeFields(mergeReview.clients).map((field, index) => (
                          <label
                            key={field}
                            className={`grid grid-cols-1 md:grid-cols-[12rem_minmax(0,1fr)] items-center gap-2 md:gap-4 px-4 py-3 ${index ? (isDark ? 'border-t border-gray-700' : 'border-t border-gray-100') : ''}`}
                          >
                            <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t(`clients.mergeFields.${field}`)}</span>
                            <select
                              value={mergeFieldSources[field] || ''}
                              onChange={event => setMergeFieldSources(prev => ({ ...prev, [field]: Number(event.target.value) }))}
                              disabled={mergeLoading}
                              className={`w-full min-w-0 rounded-md border px-3 py-2 text-sm outline-none transition-colors disabled:opacity-60 ${isDark ? 'border-gray-600 bg-[#0e1428] text-gray-100 focus:border-purple-500' : 'border-gray-300 bg-white text-gray-900 focus:border-purple-500'}`}
                            >
                              {mergeReview.clients.filter(client => mergeValue(client, field)).map(client => (
                                <option key={client.id} value={client.id}>
                                  {mergeValue(client, field)} — #{client.id} {client.name}
                                </option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
                <div className={`flex flex-col sm:flex-row sm:items-center gap-3 px-6 py-4 border-t ${isDark ? 'border-gray-700 bg-[#151a31]' : 'border-gray-200 bg-white'}`}>
                  <p className={`flex-1 text-xs leading-5 ${isDark ? 'text-amber-300/90' : 'text-amber-700'}`}>
                    {t('clients.mergeDeleteWarning').replace('{count}', mergeReview.clients.length - 1)}
                  </p>
                  <div className="flex gap-2 sm:shrink-0">
                    <button
                      type="button"
                      onClick={() => setMergeReview(null)}
                      disabled={mergeLoading}
                      className={`flex-1 sm:flex-none px-4 py-2 text-sm border rounded-md transition-colors disabled:opacity-50 ${isDark ? 'border-gray-600 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      {t('clients.mergeCancel')}
                    </button>
                    <button
                      type="button"
                      onClick={doMerge}
                      disabled={mergeLoading}
                      className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {mergeLoading ? <RefreshCw size={16} className="animate-spin" /> : <GitMerge size={16} />}
                      {mergeLoading ? t('clients.mergeInProgress') : t('clients.mergeConfirmBtn')}
                    </button>
                  </div>
                </div>
                  </>
                )}
              </div>
            ) : (
              <>
            <div className="px-6 py-5">
              <p className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{t('clients.duplicateChooseCriterion')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" role="tablist" aria-label={t('clients.duplicateChooseCriterion')}>
                {duplicateCriteria.map(({ key, label, description, Icon }) => {
                  const selected = duplicateCriterion === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => selectDuplicateCriterion(key)}
                      disabled={duplicatesLoading}
                      className={`flex items-center gap-3 p-3 text-left rounded-lg border transition-colors disabled:opacity-60 ${selected
                        ? (isDark ? 'border-purple-500 bg-purple-500/10' : 'border-purple-500 bg-purple-50')
                        : (isDark ? 'border-gray-700 bg-[#10152a] hover:border-gray-600' : 'border-gray-200 bg-gray-50 hover:border-gray-300')
                      }`}
                    >
                      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${selected
                        ? 'bg-purple-600 text-white'
                        : (isDark ? 'bg-[#252b48] text-gray-400' : 'bg-white text-gray-500')
                      }`}>
                        <Icon size={17} />
                      </span>
                      <span className="min-w-0">
                        <span className={`block text-sm font-medium ${selected ? (isDark ? 'text-purple-200' : 'text-purple-800') : (isDark ? 'text-gray-200' : 'text-gray-800')}`}>{label}</span>
                        <span className={`block mt-0.5 text-[11px] leading-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{description}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={fetchDuplicates}
                  disabled={duplicatesLoading}
                  className="inline-flex items-center justify-center gap-2 min-w-[8.5rem] px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {duplicatesLoading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />}
                  {duplicatesLoading ? t('clients.duplicateSearching') : t('clients.duplicateSearch')}
                </button>
              </div>
            </div>
            <div className={`min-h-0 flex-1 px-6 py-5 overflow-y-auto border-t ${isDark ? 'border-gray-700/80 bg-[#11162b]' : 'border-gray-200 bg-gray-50/70'}`}>
              {!duplicatesSearched ? (
                <div className={`py-8 text-center text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {t('clients.duplicateReady')}
                </div>
              ) : duplicatesLoading ? (
                <div className={`flex items-center justify-center gap-2 py-12 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <RefreshCw size={17} className="animate-spin" />
                  {t('clients.duplicateSearching')}
                </div>
              ) : duplicates.length === 0 ? (
                <div className="py-10 text-center">
                  <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full ${isDark ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>
                    <Search size={18} />
                  </div>
                  <p className={`mt-3 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('clients.duplicatesEmpty')}</p>
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{t('clients.duplicatesEmptyHint')}</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('clients.duplicateResults')}</p>
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{duplicates.length} {t('clients.duplicatesGroups')}</span>
                  </div>
                  <div className="space-y-3">
                    {duplicates.map((g, gi) => {
                      const meta = dupTypeMeta[g.type] || dupTypeMeta.name;
                      const Icon = meta.Icon;
                      const groupKey = `${g.type}-${g.value}-${gi}`;
                      return (
                        <div key={groupKey} className={`overflow-hidden rounded-lg border ${isDark ? 'border-gray-700 bg-[#171d36]' : 'border-gray-200 bg-white'}`}>
                          <div className={`flex items-center gap-2 px-4 py-2.5 border-b text-xs ${isDark ? 'border-gray-700 bg-[#0e1428] text-gray-300' : 'border-gray-200 bg-gray-50 text-gray-600'}`}>
                            <Icon size={14} className="shrink-0 text-purple-400" />
                            <span className="font-medium whitespace-nowrap">{meta.label}:</span>
                            <span className={`truncate font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{g.value}</span>
                            <span className={`ml-auto shrink-0 px-2 py-0.5 rounded-full font-medium ${isDark ? 'bg-purple-500/15 text-purple-300' : 'bg-purple-100 text-purple-700'}`}>{g.count}</span>
                          </div>
                          <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
                            {g.clients.map(c => (
                                <div
                                  key={c.id}
                                  className="flex items-center gap-3 px-4 py-3 text-sm"
                                >
                                  <button
                                    onClick={() => openDuplicateClient(c.id)}
                                    className={`flex-1 min-w-0 grid grid-cols-[auto_minmax(7rem,0.8fr)_minmax(9rem,1.4fr)] md:grid-cols-[auto_minmax(8rem,0.8fr)_minmax(10rem,1.4fr)_minmax(7rem,0.7fr)] items-center gap-x-3 gap-y-1 text-left rounded px-1 transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                                  >
                                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>#{c.id}</span>
                                    <span className={`truncate font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{c.name}</span>
                                    <span className={`truncate text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{c.email || '—'}</span>
                                    <span className={`hidden md:block truncate text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{c.phone || '—'}</span>
                                  </button>
                                </div>
                              ))}
                          </div>
                          {canMerge && g.clients.length >= 2 && (
                            <div className={`px-4 py-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                              <button
                                onClick={() => openMergeReview(groupKey, g.clients)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700"
                              >
                                <GitMerge size={13} />
                                {t('clients.mergeReviewButton').replace('{count}', g.clients.length)}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
